import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { CreditCard, Link2, Send, Copy, Loader2, Check, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { getStripe, isStripeConfigured } from "@/lib/stripe";
import { toast } from "sonner";
import { InfoButton, type InfoTopic } from "@/components/info-button";
import { CardCheckFailed, CardCheckedLine, CheckingCard, cardCheckOutcome } from "@/components/card-check";

/** ⓘ next to "Card checked" (card hold step 2). Plain words, no code. */
export const CARD_CHECK_HELP: InfoTopic = {
    title: 'What "Card checked" means',
    lines: [
        "When a card is saved, Stripe asks the customer's bank if the card is real and open. No money moves.",
        "Green means the bank said yes. The card is saved on the customer.",
        "Red means the bank said no. Nothing was saved and nothing was charged. Try another card, or send the customer a secure link.",
        "A good check doesn't promise the money will be there on the day of the visit. The card is still charged only after the work is done.",
    ],
    shots: [
        {
            src: "/how-to/card-check-green.png",
            alt: "The green Card checked line",
            marks: [{ x: 92, y: 50, label: "Green check: the bank said yes. The card is saved.", side: "left" }],
        },
        {
            src: "/how-to/card-check-red.png",
            alt: "The red Card didn't pass the check box",
            marks: [
                { x: 50, y: 22, label: "Red: the bank said no. Nothing saved, nothing charged.", side: "below" },
                { x: 50, y: 80, label: "Try another card, or send the customer a secure link.", side: "above" },
            ],
        },
    ],
};

type SavedCard = { brand: string | null; last4: string | null };

interface Props {
    customerId: string;
    hasCard?: boolean;
    brand?: string | null;
    last4?: string | null;
    /** Fired after a card is saved inline, so callers can refresh their own
     *  read models (e.g. the Closeout payment gate). Optional. */
    onCardSaved?: () => void;
}

function InlineCardForm({
    customerId,
    clientSecret,
    onSaved,
}: {
    customerId: string;
    clientSecret: string;
    onSaved: (card: SavedCard) => void;
}) {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [failed, setFailed] = useState<string | null>(null);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!stripe || !elements) return;
        const card = elements.getElement(CardElement);
        if (!card) return;
        setSubmitting(true);
        setError(null);
        try {
            // Stripe asks the bank to check the card. No money moves.
            const result = await stripe.confirmCardSetup(clientSecret, { payment_method: { card } });
            const outcome = cardCheckOutcome(result, "office");
            if (outcome.kind === "failed") {
                setFailed(outcome.reason);
                setSubmitting(false);
                return;
            }
            if (outcome.kind !== "passed") {
                setError(outcome.message);
                setSubmitting(false);
                return;
            }
            const saved = await api.post<{ brand?: string | null; last4?: string | null }>(
                `/api/customers/${customerId}/card-on-file`,
                { paymentMethodId: outcome.paymentMethodId },
            );
            onSaved({ brand: saved?.brand ?? null, last4: saved?.last4 ?? null });
        } catch {
            setError("The bank checked the card, but it didn't save. Nothing was charged. Try again.");
            setSubmitting(false);
        }
    }

    if (failed) {
        return (
            <CardCheckFailed
                variant="office"
                reason={failed}
                nextStep="Try another card, or send the customer a secure link."
                onTryAgain={() => setFailed(null)}
            />
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="rounded-md border border-input bg-background px-3 py-3">
                <CardElement options={{ style: { base: { fontSize: "16px" } } }} />
            </div>
            {error && (
                <p className="text-xs text-red-600" role="alert" data-testid="office-card-error">
                    {error}
                </p>
            )}
            {submitting && <CheckingCard variant="office" />}
            <div className="flex justify-end">
                <Button type="submit" disabled={!stripe || submitting}>
                    {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
                    {submitting ? "Checking…" : "Save card"}
                </Button>
            </div>
        </form>
    );
}

export default function CardOnFilePanel({ customerId, hasCard, brand, last4, onCardSaved }: Props) {
    const qc = useQueryClient();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [starting, setStarting] = useState(false);
    const [sending, setSending] = useState(false);
    const [copying, setCopying] = useState(false);
    // The bank's answer, shown in the dialog before it closes (card hold step 2).
    const [checked, setChecked] = useState<SavedCard | null>(null);

    async function openInline() {
        setStarting(true);
        try {
            const r = await api.post<{ clientSecret: string }>(`/api/customers/${customerId}/setup-intent`, {});
            setClientSecret(r.clientSecret);
            setDialogOpen(true);
        } catch {
            toast.error("Could not start card entry.");
        } finally {
            setStarting(false);
        }
    }

    function onSaved(card: SavedCard) {
        setChecked(card);
        setClientSecret(null);
        qc.invalidateQueries({ queryKey: ["customer", customerId] });
        onCardSaved?.();
    }

    function closeDialog() {
        setDialogOpen(false);
        setClientSecret(null);
        setChecked(null);
    }

    async function sendLink() {
        setSending(true);
        try {
            const r = await api.post<{ sent: { sms: boolean; email: boolean } }>(
                `/api/customers/${customerId}/send-card-link`,
                {},
            );
            const ch = [r.sent.sms && "SMS", r.sent.email && "email"].filter(Boolean).join(" + ");
            toast.success(ch ? `Secure link sent via ${ch}.` : "Link generated, but no delivery channel succeeded.");
        } catch {
            toast.error("Could not send the card link.");
        } finally {
            setSending(false);
        }
    }

    async function copyLink() {
        setCopying(true);
        try {
            const r = await api.get<{ url: string }>(`/api/customers/${customerId}/card-link`);
            await navigator.clipboard.writeText(r.url);
            toast.success("Card link copied.");
        } catch {
            toast.error("Could not copy the link.");
        } finally {
            setCopying(false);
        }
    }

    const stripeReady = isStripeConfigured();

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                    <CreditCard className="h-4 w-4" /> Card on File
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
                {hasCard ? (
                    <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-emerald-500" />
                        <span className="font-medium capitalize">{brand ?? "Card"}</span>
                        {last4 && <span className="text-foreground/80">ending in {last4}</span>}
                    </div>
                ) : (
                    <p className="text-foreground/80">No card on file.</p>
                )}

                <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={openInline} disabled={!stripeReady || starting}>
                        {starting ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                            <CreditCard className="h-4 w-4 mr-1" />
                        )}
                        {hasCard ? "Replace card" : "Enter card now"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={sendLink} disabled={!stripeReady || sending}>
                        {sending ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                            <Send className="h-4 w-4 mr-1" />
                        )}
                        Send secure link
                    </Button>
                    <Button size="sm" variant="ghost" onClick={copyLink} disabled={copying}>
                        {copying ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                            <Copy className="h-4 w-4 mr-1" />
                        )}
                        Copy link
                    </Button>
                </div>

                {!stripeReady && (
                    <p className="text-xs text-foreground/60 flex items-center gap-1">
                        <Link2 className="h-3 w-3" /> Stripe is not configured for this build.
                    </p>
                )}

                <Dialog open={dialogOpen} onClose={closeDialog} title={checked ? "Card saved" : "Enter card securely"}>
                    <div className="space-y-4">
                        {checked ? (
                            <div className="space-y-4" data-testid="office-card-checked">
                                <CardCheckedLine
                                    variant="office"
                                    brand={checked.brand}
                                    last4={checked.last4}
                                    trailing={<InfoButton topic={CARD_CHECK_HELP} />}
                                />
                                <p className="text-sm text-muted-foreground">
                                    It&apos;s saved on this customer. Nothing was charged.
                                </p>
                                <div className="flex justify-end">
                                    <Button type="button" onClick={closeDialog}>
                                        Done
                                    </Button>
                                </div>
                            </div>
                        ) : clientSecret ? (
                            <Elements stripe={getStripe()} options={{ appearance: { theme: "stripe" } }}>
                                <InlineCardForm customerId={customerId} clientSecret={clientSecret} onSaved={onSaved} />
                            </Elements>
                        ) : (
                            <div className="py-6 flex justify-center">
                                <Loader2 className="h-5 w-5 animate-spin text-foreground/70" />
                            </div>
                        )}
                        <p className="text-[11px] text-foreground/60 flex items-center gap-1">
                            <Lock className="h-3 w-3" /> Card details are handled directly by Stripe.
                        </p>
                    </div>
                </Dialog>
            </CardContent>
        </Card>
    );
}
